// Shared competitor config-row spec + formatters + generic detail types, used by
// both TemplateCompetitorDetailsTable (report templates) and AutomaticReportDetailsTable
// (automatic reports). The competitor payloads have identical field names in both cases
// (the AR embeds its private template's competitor summary), so the mapping is shared.

import { ReportTemplateCompetitor } from "@/models";
import { granularityLabelFor } from "./granularity";
import { EMPTY } from "./emptyValue";

export type DetailRow = {
  label: string;
  value: string;
  title?: string;
};

export type DetailSection = {
  title: string;
  rows: DetailRow[];
};

type RowSpec = {
  label: string;
  visible: (competitor: ReportTemplateCompetitor) => boolean;
  value: (competitor: ReportTemplateCompetitor) => string;
  title?: (competitor: ReportTemplateCompetitor) => string | undefined;
};

// Defined in its own leaf module so pure helpers can import it without dragging in this
// file's ReportBuilderMixin dependency. Re-exported here so existing importers keep working.
export { EMPTY };

// Shared "Informazioni generali" formatters. Parameter types are structural, not model
// types, so they serve both hydrated models and the plain camelCase objects an automatic
// report job embeds in its `data` snapshot.

// Backend dates arrive as YYYY-MM-DD; show DD/MM/YYYY without pulling in a date lib here.
export const formatBackendDate = (value?: string | null): string => {
  if (!value) return EMPTY;
  const parts = value.split("-");
  return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : value;
};

export const profileLabel = (
  profile?: { name?: string; slug?: string } | null
): string => {
  if (!profile) return EMPTY;
  return profile.slug
    ? `${profile.name} (${profile.slug})`
    : profile.name || EMPTY;
};

export const userLabel = (user?: { email?: string } | null): string =>
  user?.email || EMPTY;

export const timeSlotsValue = (
  filter?: { from: string; to: string }[] | null
): string => {
  if (!filter || !filter.length) return "no";
  return filter.map((slot) => `${slot.from}–${slot.to}`).join(", ");
};

const MAX_VISIBLE_IDS = 4;

const UNIT_LABELS: Record<string, string> = {
  seconds: "N° Secondi",
  spots: "N° Spot",
  audience: "Contatti",
  grp: "GRP",
  spots_and_seconds: "N° Spot e N° Secondi",
};

const hasList = (value: unknown): boolean =>
  Array.isArray(value) && value.length > 0;

const formatIdList = (value: unknown): string => {
  if (!Array.isArray(value) || !value.length) return EMPTY;
  if (value.includes("all")) return "Tutti";
  const shown = value.slice(0, MAX_VISIBLE_IDS).join(", ");
  const hidden = value.length - MAX_VISIBLE_IDS;
  return hidden > 0 ? `${shown}… [+${hidden} altri]` : shown;
};

const resolveNames = (
  ids: unknown,
  names?: Record<string, string>
): string[] | null => {
  if (!Array.isArray(ids) || !ids.length || ids.includes("all")) return null;
  return ids.map((id) => names?.[String(id)] ?? `#${id}`);
};

const formatNamedIdList = (
  ids: unknown,
  names?: Record<string, string>
): string => {
  if (!Array.isArray(ids) || !ids.length) return EMPTY;
  if (ids.includes("all")) return "Tutti";
  const labels = resolveNames(ids, names) as string[];
  const shown = labels.slice(0, MAX_VISIBLE_IDS).join(", ");
  const hidden = labels.length - MAX_VISIBLE_IDS;
  return hidden > 0 ? `${shown}… [+${hidden} altri]` : shown;
};

const namedListTooltip = (
  ids: unknown,
  names?: Record<string, string>
): string | undefined => {
  const labels = resolveNames(ids, names);
  if (!labels || labels.length <= MAX_VISIBLE_IDS) return undefined;
  return labels.join(", ");
};

const formatBool = (value: unknown): string => (value ? "sì" : "no");

const unitLabel = (value: string): string => UNIT_LABELS[value] ?? value;

const targetAuditelValue = (competitor: ReportTemplateCompetitor): string => {
  const target = competitor.grpTarget;
  if (target?.id && target.name) return target.name;
  return competitor.auditelTarget != null
    ? String(competitor.auditelTarget)
    : EMPTY;
};

const CONFIG_ROWS: RowSpec[] = [
  {
    label: "Emittenti",
    visible: (c) => hasList(c.competitors),
    value: (c) => formatNamedIdList(c.competitors, c.competitorsNames),
    title: (c) => namedListTooltip(c.competitors, c.competitorsNames),
  },
  {
    label: "Concessionarie",
    visible: (c) => hasList(c.agencies),
    value: (c) => formatNamedIdList(c.agencies, c.agenciesNames),
    title: (c) => namedListTooltip(c.agencies, c.agenciesNames),
  },
  {
    label: "Aziende",
    visible: (c) => hasList(c.companies),
    value: (c) => formatIdList(c.companies),
  },
  {
    label: "Brand",
    visible: (c) => hasList(c.brands),
    value: (c) => formatIdList(c.brands),
  },
  {
    label: "Prodotti",
    visible: (c) => hasList(c.products),
    value: (c) => formatIdList(c.products),
  },
  {
    label: "Settore/Categoria/Classe",
    visible: (c) => c.attachSectorCategoryClass === true,
    value: () => "sì",
  },
  {
    label: "Anagrafica Cliente",
    visible: (c) => c.applyClientDatabase === true,
    value: () => "sì",
  },
  {
    label: "Settori",
    visible: (c) => hasList(c.sectors),
    value: (c) => formatIdList(c.sectors),
  },
  {
    label: "Categorie",
    visible: (c) => hasList(c.categories),
    value: (c) => formatIdList(c.categories),
  },
  {
    label: "Classi",
    visible: (c) => hasList(c.classes),
    value: (c) => formatIdList(c.classes),
  },
  {
    label: "Formato (Completo / RAW Data)",
    visible: () => true,
    value: (c) => (c.raw ? "RAW Data" : "Completo"),
  },
  {
    label: "KPI",
    visible: () => true,
    value: (c) => unitLabel(c.type),
  },
  {
    label: "Target Auditel",
    visible: (c) => c.auditelTarget != null,
    value: (c) => targetAuditelValue(c),
  },
  {
    label: "Dettaglio Formato",
    visible: () => true,
    value: (c) => formatBool(c.formatDetail),
  },
  {
    label: "Dettaglio Tabellare/Sponsorizzazioni",
    visible: () => true,
    value: (c) => formatBool(c.showSpecialInitiatives),
  },
  {
    label: "Dettaglio Temporale",
    visible: () => true,
    value: (c) => formatBool(c.temporalDetail),
  },
  {
    label: "Granularità",
    visible: (c) => c.temporalDetail === true,
    value: (c) => granularityLabelFor(c.granularity),
  },
  {
    label: "Inclusion",
    visible: () => true,
    value: (c) => formatBool(c.inclusion),
  },
  {
    label: "Quote",
    visible: () => true,
    value: (c) => formatBool(c.quota),
  },
  {
    label: "Breakdown Device",
    visible: (c) => c.showVodDevices === true,
    value: () => "sì",
  },
];

export function buildCompetitorDetailRows(
  competitor: ReportTemplateCompetitor | Record<string, unknown>
): DetailRow[] {
  const c = competitor as ReportTemplateCompetitor;
  return CONFIG_ROWS.filter((row) => row.visible(c)).map((row) => ({
    label: row.label,
    value: row.value(c),
    title: row.title?.(c),
  }));
}
